import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, Receipt, Home, Loader2 } from 'lucide-react'
import Logo from '@/components/Logo'
import { api } from '@/lib/api'

export function PaymentSuccessPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('orderId')
  const paymentId = searchParams.get('paymentId')
  const paymentIntentId = searchParams.get('payment_intent')

  const [countdown, setCountdown] = useState(10)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    // Always try to confirm and submit when we have an orderId
    if (orderId) {
      confirmPaymentAndSubmitOrder()
    }
  }, [orderId])

  const confirmPaymentAndSubmitOrder = async () => {
    if (confirming) return
    setConfirming(true)

    try {
      console.log('Processing order:', orderId)

      // Get payment intent from query params (Stripe adds this on redirect)
      const stripePaymentIntent = paymentIntentId

      if (stripePaymentIntent) {
        console.log('Confirming Stripe payment intent:', stripePaymentIntent)

        // Step 1: Confirm payment on backend
        try {
          await api.post('/api/payments/public/confirm', {
            paymentIntentId: stripePaymentIntent
          })
          console.log('Payment confirmed!')
        } catch (err: any) {
          console.log('Payment confirmation failed or already confirmed:', err.response?.data)
          // Continue anyway - payment might already be confirmed
        }
      }

      // Step 2: Submit order to kitchen (always try this)
      console.log('Submitting order to kitchen...')
      try {
        await api.post(`/api/public/order/${orderId}/submit`)
        console.log('Order submitted successfully!')
      } catch (err: any) {
        console.error('Failed to submit order:', err.response?.data)
        // If already submitted, that's ok
        if (err.response?.status !== 400) {
          throw err
        }
      }
    } catch (error) {
      console.error('Error processing order:', error)
    } finally {
      setConfirming(false)
    }
  }

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          // Redirect to table link if available, otherwise home
          const tableLink = localStorage.getItem('qorder_table_link')
          navigate(tableLink || '/')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [navigate])

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Logo size={36} variant="full" />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-12 text-center">
            <div className="inline-block p-4 bg-white/20 rounded-full mb-4 backdrop-blur-sm">
              <CheckCircle size={64} strokeWidth={2} />
            </div>
            <h1 className="text-4xl font-bold mb-2">Pagamento Completato!</h1>
            <p className="text-green-100 text-lg">
              Il tuo ordine è stato pagato con successo
            </p>
          </div>

          {/* Body */}
          <div className="px-8 py-8">
            {/* Order Info */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Dettagli Ordine</h2>

              {orderId && (
                <div className="flex justify-between mb-3 pb-3 border-b border-gray-200">
                  <span className="text-gray-600">ID Ordine</span>
                  <span className="font-mono text-sm font-medium text-gray-900">
                    #{orderId.substring(0, 8).toUpperCase()}
                  </span>
                </div>
              )}

              {paymentId && (
                <div className="flex justify-between mb-3 pb-3 border-b border-gray-200">
                  <span className="text-gray-600">ID Pagamento</span>
                  <span className="font-mono text-sm font-medium text-gray-900">
                    #{paymentId.substring(0, 8).toUpperCase()}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Stato</span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  Pagato
                </span>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Cosa succede ora?</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Email di Conferma</h3>
                    <p className="text-sm text-gray-600">
                      Riceverai una ricevuta via email a breve con tutti i dettagli del pagamento.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Preparazione Ordine</h3>
                    <p className="text-sm text-gray-600">
                      Il ristorante è stato notificato e inizierà a preparare il tuo ordine.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-semibold">
                    3
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Servizio al Tavolo</h3>
                    <p className="text-sm text-gray-600">
                      Il tuo ordine verrà servito non appena pronto. Buon appetito!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  const tableLink = localStorage.getItem('qorder_table_link')
                  navigate(tableLink || '/')
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center justify-center gap-3 shadow-lg"
              >
                <Home size={24} />
                <span>Torna al Menu</span>
              </button>

              {orderId && (
                <button
                  onClick={() => navigate(`/order/${orderId}`)}
                  className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition flex items-center justify-center gap-2"
                >
                  <Receipt size={20} />
                  <span>Visualizza Ricevuta</span>
                </button>
              )}
            </div>

            {/* Auto Redirect Info */}
            <div className="mt-6 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
                <Loader2 className="animate-spin" size={16} />
                <span>Reindirizzamento automatico tra {countdown} secondi...</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Grazie per aver utilizzato <span className="font-semibold text-gray-900">Qorder</span>!
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Per assistenza, contatta direttamente il ristorante
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Il pagamento è stato elaborato in modo sicuro tramite Stripe.</p>
          <p className="mt-1">I tuoi dati di pagamento non sono mai stati memorizzati sui nostri server.</p>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccessPage
